import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import { SyncAdminService, SyncDeviceCreated } from '../../../../services/openapi';

interface INewDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function NewDeviceDialog({ open, onClose, onCreated }: INewDeviceDialogProps) {
  const { call_backend_api } = React.useContext(APIWrapperContext);
  const [name, setName] = React.useState('');
  const [token, setToken] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const handleCreate = () => {
    call_backend_api(
      SyncAdminService.createSyncDeviceSyncAdminDevicesPost({ name: name.trim() }),
      (res: SyncDeviceCreated) => {
        setToken(res.token);
        onCreated();
      },
      undefined,
      undefined,
      true,
      'Sync-Gerät wurde angelegt',
      'Fehler beim Anlegen des Sync-Geräts!'
    );
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClose = () => {
    setName('');
    setToken('');
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Neues Sync-Gerät anlegen</DialogTitle>
      <DialogContent>
        {token === '' ? (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Vergeben Sie einen eindeutigen Namen für den Kunden-PC (z.B. "Büro Empfang").
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Gerätename"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim() !== '') handleCreate();
              }}
            />
          </>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Dieser Token wird nur einmal angezeigt. Kopieren Sie ihn jetzt und tragen Sie ihn in
              der Sync-App auf dem Kunden-PC ein.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                {token}
              </Typography>
              <IconButton onClick={handleCopy} title="Token kopieren">
                {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
              </IconButton>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {token === '' ? (
          <>
            <Button onClick={handleClose}>Abbrechen</Button>
            <Button variant="contained" disabled={name.trim() === ''} onClick={handleCreate}>
              Anlegen
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            Fertig
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
